import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import LanguageSwitcher from './LanguageSwitcher';
import UserMenu from './UserMenu';
import SplineBackground from './SplineBackground';
import { SITE_URL, SITE_NAME } from './SEOHead';
import {
  getLayoutSplineSceneUrl,
  shouldShowLayoutSplineBackground,
} from '../config/spline';
import './Layout.css';

const WEBSITE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
};

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const IconEmail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" aria-hidden>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isHistoryDropdownOpen, setIsHistoryDropdownOpen] = useState(false);
  const [footerContact, setFooterContact] = useState({ contact_email: '', contact_phone: '', social_links: {} });
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  const location = useLocation();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleViewportChange = () => setIsMobileViewport(mediaQuery.matches);

    handleViewportChange();
    mediaQuery.addEventListener('change', handleViewportChange);
    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await api.get('/content/contact-page/');
        setFooterContact({
          contact_email: res.data.contact_email || '',
          contact_phone: res.data.contact_phone || '',
          social_links: res.data.social_links || {},
        });
      } catch {
        // use empty defaults
      }
    };
    fetchContact();
  }, []);

  const isAboutActive =
    location.pathname === '/about' ||
    location.pathname === '/team' ||
    location.pathname === '/contact';

  const isProductionActive =
    location.pathname === '/services' ||
    location.pathname.startsWith('/services/') ||
    location.pathname.startsWith('/about/equipment');

  const isHistoryActive = location.pathname.startsWith('/history/');
  const isHomePage = location.pathname === '/';
  const isListSplinePage = shouldShowLayoutSplineBackground(location.pathname);
  const showDroneOverlay = !isMobileViewport && (isHomePage || isListSplinePage);
  const isSplinePage = isHomePage || (isListSplinePage && !isMobileViewport);
  const layoutSplineSceneUrl = getLayoutSplineSceneUrl();

  const sameAs = Object.values(footerContact.social_links || {}).filter(Boolean);
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/GFM_White.png`,
    ...(sameAs.length > 0 && { sameAs }),
  };

  const toggleMobileMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu and dropdown when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAboutDropdownOpen(false);
    setIsHistoryDropdownOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open using CSS class
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.documentElement.classList.add('mobile-menu-open');
      document.body.classList.add('mobile-menu-open');
    } else {
      document.documentElement.classList.remove('mobile-menu-open');
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.documentElement.classList.remove('mobile-menu-open');
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMobileMenuOpen]);

  return (
    <div className={`layout${isSplinePage ? ' layout--with-spline' : ''}`}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBSITE_JSON_LD)}</script>
      </Helmet>
      <nav className="navbar">
        <div className="container">
          <Link to="/" className="logo">
            <img src="/GFM_White.png" alt="GFM Studio" className="logo-img" />
          </Link>
          
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleMobileMenu(e);
            }}
            aria-label="Toggle mobile menu"
            type="button"
          >
            ☰
          </button>
          
          <div className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
            {isMobileMenuOpen && (
              <button 
                className="mobile-menu-close"
                onClick={toggleMobileMenu}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleMobileMenu(e);
                }}
                aria-label="Close mobile menu"
                type="button"
              >
                ✕
              </button>
            )}
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>{t('nav.home')}</Link>
            <div
              className={`nav-dropdown ${isAboutDropdownOpen ? 'open' : ''} ${isAboutActive ? 'active' : ''}`}
              onMouseEnter={() => {
                if (window.innerWidth >= 769) {
                  setIsAboutDropdownOpen(true);
                }
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 769) {
                  setIsAboutDropdownOpen(false);
                }
              }}
            >
              <button
                type="button"
                className="nav-dropdown-trigger"
                onClick={() => setIsAboutDropdownOpen(!isAboutDropdownOpen)}
                aria-expanded={isAboutDropdownOpen}
                aria-haspopup="true"
              >
                {i18n.language === 'ka' ? 'ჩვენ შესახებ' : 'About Us'}
                <span className="nav-dropdown-arrow">▼</span>
              </button>
              <div className="nav-dropdown-menu">
                <Link to="/about" onClick={() => { setIsMobileMenuOpen(false); setIsAboutDropdownOpen(false); }}>
                  {i18n.language === 'ka' ? 'კომპანია' : 'Company'}
                </Link>
                <Link to="/team" onClick={() => { setIsMobileMenuOpen(false); setIsAboutDropdownOpen(false); }}>
                  {i18n.language === 'ka' ? 'ჩვენი გუნდი' : 'Our Team'}
                </Link>
                <Link to="/contact" onClick={() => { setIsMobileMenuOpen(false); setIsAboutDropdownOpen(false); }}>
                  {i18n.language === 'ka' ? 'კონტაქტი' : 'Contact'}
                </Link>
              </div>
            </div>
            <div
              className={`nav-dropdown nav-dropdown-history ${isHistoryDropdownOpen ? 'open' : ''} ${isHistoryActive ? 'active' : ''}`}
              onMouseEnter={() => {
                if (window.innerWidth >= 769) {
                  setIsHistoryDropdownOpen(true);
                }
              }}
              onMouseLeave={() => {
                if (window.innerWidth >= 769) {
                  setIsHistoryDropdownOpen(false);
                }
              }}
            >
              <button
                type="button"
                className="nav-dropdown-trigger"
                onClick={() => setIsHistoryDropdownOpen(!isHistoryDropdownOpen)}
                aria-expanded={isHistoryDropdownOpen}
                aria-haspopup="true"
              >
                {i18n.language === 'ka' ? 'ისტორია' : 'History'}
                <span className="nav-dropdown-arrow">▼</span>
              </button>
              <div className="nav-dropdown-menu">
                <Link to="/history/our-work" onClick={() => { setIsMobileMenuOpen(false); setIsHistoryDropdownOpen(false); }}>
                  {i18n.language === 'ka' ? 'ნამუშევრები' : 'Our Work'}
                </Link>
                <Link to="/history/blogs" onClick={() => { setIsMobileMenuOpen(false); setIsHistoryDropdownOpen(false); }}>
                  {i18n.language === 'ka' ? 'ბლოგები' : 'Blogs'}
                </Link>
              </div>
            </div>
            <Link
              to="/services"
              className={`nav-services-link ${isProductionActive ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {i18n.language === 'ka' ? 'სერვისები' : 'Services'}
            </Link>
            <LanguageSwitcher />
            <UserMenu onLinkClick={() => setIsMobileMenuOpen(false)} />
          </div>
        </div>
      </nav>
      
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={toggleMobileMenu}
          onTouchEnd={(e) => {
            e.preventDefault();
            toggleMobileMenu(e);
          }}
        />
      )}
      
      <main className={`main-content${isSplinePage ? ' main-content--with-spline' : ''}`}>
        {children}
      </main>
      
        <footer className={`footer ${location.pathname === '/about' || location.pathname === '/contact' || location.pathname === '/privacy' || location.pathname.startsWith('/services/') || location.pathname.startsWith('/about/equipment') || location.pathname.startsWith('/history/') ? 'footer-overlay' : ''}`}>
        <div className="container">
          <div className="footer-content">
            <div className="footer-info">
              <nav className="footer-links" aria-label={i18n.language === 'ka' ? 'ქვედა ნავიგაცია' : 'Footer navigation'}>
                <Link to="/services">{i18n.language === 'ka' ? 'სერვისები' : 'Services'}</Link>
                <span className="footer-links-dot" aria-hidden>&bull;</span>
                <Link to="/history/our-work">{i18n.language === 'ka' ? 'ნამუშევრები' : 'Our Work'}</Link>
                <span className="footer-links-dot" aria-hidden>&bull;</span>
                <Link to="/history/blogs">{i18n.language === 'ka' ? 'ბლოგები' : 'Blog'}</Link>
                <span className="footer-links-dot" aria-hidden>&bull;</span>
                <Link to="/about">{i18n.language === 'ka' ? 'ჩვენ შესახებ' : 'About'}</Link>
                <span className="footer-links-dot" aria-hidden>&bull;</span>
                <Link to="/contact">{i18n.language === 'ka' ? 'კონტაქტი' : 'Contact'}</Link>
                <span className="footer-links-dot" aria-hidden>&bull;</span>
                <Link to="/privacy">{i18n.language === 'ka' ? 'კონფიდენციალურობა' : 'Privacy'}</Link>
              </nav>
              <span className="footer-divider" aria-hidden />
              <div className="footer-bottom-row">
                <img src="/GFM_White.png" alt="GFM Studio" className="footer-logo" />
                {(footerContact.contact_phone || footerContact.contact_email) && (
                  <div className="footer-contact">
                    {footerContact.contact_phone && (
                      <a href={`tel:${footerContact.contact_phone}`} className="footer-contact-item" title="Phone">
                        <span className="footer-contact-icon"><IconPhone /></span>
                        <span>{footerContact.contact_phone}</span>
                      </a>
                    )}
                    {footerContact.contact_phone && footerContact.contact_email && (
                      <span className="footer-contact-sep" aria-hidden>|</span>
                    )}
                    {footerContact.contact_email && (
                      <a href={`mailto:${footerContact.contact_email}`} className="footer-contact-item" title="Email">
                        <span className="footer-contact-icon"><IconEmail /></span>
                        <span>{footerContact.contact_email}</span>
                      </a>
                    )}
                  </div>
                )}
                <span className="footer-contact-sep" aria-hidden>|</span>
                <p>&copy; 2026 GFM Studio. {t('common.allRightsReserved')}.</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {showDroneOverlay && (
        <SplineBackground sceneUrl={layoutSplineSceneUrl} className="spline-overlay--drone-top" />
      )}
    </div>
  );
};

export default Layout;

